/**
 * Modal com lista de leads do grupo
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  IconButton,
  InputAdornment,
  TextField
} from '@mui/material';
import { CloseOutlined, SearchOutlined } from '@mui/icons-material';
import { LeadGroup, Lead } from '../../store/slices/groupsSlice';
import LeadFollowUpModal from './LeadFollowUpModal';

interface LeadListModalProps {
  open: boolean;
  onClose: () => void;
  group: LeadGroup;
}

const LeadListModal: React.FC<LeadListModalProps> = ({ open, onClose, group }) => {
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'novo': 'Novo',
      'contactado': 'Contactado',
      'recusou': 'Recusou',
      'sem_contato': 'Sem Contato',
      'semContato': 'Sem Contato',
      'em_contato': 'Em Contato',
      'emContato': 'Em Contato',
      'fechado': 'Fechado'
    };
    return labels[status] || 'Novo';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'novo': '#757575',
      'contactado': '#2196f3',
      'recusou': '#f44336',
      'sem_contato': '#ff9800',
      'semContato': '#ff9800',
      'em_contato': '#9c27b0',
      'emContato': '#9c27b0',
      'fechado': '#4caf50'
    };
    return colors[status] || '#757575';
  };

  const getWebsiteType = (website?: string) => {
    if (!website) return 'sem-site';
    
    const url = website.toLowerCase();
    if (url.includes('instagram.com') || url.includes('instagr.am')) return 'instagram';
    if (url.includes('facebook.com') || url.includes('fb.com') || url.includes('fb.me')) return 'facebook';
    
    return 'site';
  };

  const getWebsiteLabel = (type: string) => {
    const labels = {
      'sem-site': 'Não tem site',
      'instagram': 'Instagram',
      'facebook': 'Facebook',
      'site': 'Site'
    };
    return labels[type as keyof typeof labels] || 'Site';
  };

  const getWebsiteIcon = (type: string) => {
    const icons = {
      'sem-site': '🚫',
      'instagram': '📷',
      'facebook': '👥',
      'site': '🌐'
    };
    return icons[type as keyof typeof icons] || '🌐';
  };

  const filteredLeads = group.leads.filter((lead) =>
    lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a1a',
            border: '1px solid rgba(255, 107, 53, 0.2)',
            maxHeight: '80vh',
          }
        }}
      >
        <DialogTitle
          sx={{
            color: '#FFFFFF',
            borderBottom: '1px solid rgba(255, 107, 53, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="h6">{group.name}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              {group.leads.length} leads no grupo
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            <CloseOutlined />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {/* Search */}
          <TextField
            fullWidth
            placeholder="Buscar lead..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: '#FFFFFF',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 107, 53, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#FF6B35',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Leads List */}
          <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {filteredLeads.map((lead) => (
              <ListItemButton
                key={lead.id}
                onClick={() => setSelectedLead(lead)}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 107, 53, 0.1)',
                    borderColor: 'rgba(255, 107, 53, 0.3)',
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                        {lead.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {/* Website Badge */}
                        {(() => {
                          const websiteType = getWebsiteType(lead.website);
                          return (
                            <Chip
                              label={`${getWebsiteIcon(websiteType)} ${getWebsiteLabel(websiteType)}`}
                              size="small"
                              sx={{
                                backgroundColor: websiteType === 'sem-site' 
                                  ? 'rgba(158, 158, 158, 0.2)' 
                                  : websiteType === 'instagram'
                                  ? 'rgba(225, 48, 108, 0.2)'
                                  : websiteType === 'facebook'
                                  ? 'rgba(24, 119, 242, 0.2)'
                                  : 'rgba(76, 175, 80, 0.2)',
                                color: websiteType === 'sem-site'
                                  ? '#9e9e9e'
                                  : websiteType === 'instagram'
                                  ? '#E1306C'
                                  : websiteType === 'facebook'
                                  ? '#1877F2'
                                  : '#4caf50',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: '24px',
                              }}
                            />
                          );
                        })()}
                        
                        {/* Status Badge */}
                        <Chip
                          label={getStatusLabel(lead.leadStatus || 'novo')}
                          size="small"
                          sx={{
                            backgroundColor: `${getStatusColor(lead.leadStatus || 'novo')}20`,
                            color: getStatusColor(lead.leadStatus || 'novo'),
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: '24px',
                          }}
                        />
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Box>
                      {/* Cidade e Estado */}
                      {(lead.city || lead.state) && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#FF6B35', 
                            fontWeight: 600,
                            mb: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          📍 {lead.city}{lead.state ? ` - ${lead.state}` : ''}
                        </Typography>
                      )}
                      
                      {/* Endereço */}
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                        {lead.address}
                      </Typography>
                      
                      {/* Contatos */}
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                        {lead.phone && (
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                            📞 {lead.phone}
                          </Typography>
                        )}
                        {lead.website && getWebsiteType(lead.website) === 'site' && (
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                            🌐 Site
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>

          {filteredLeads.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Nenhum lead encontrado
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Follow-up */}
      {selectedLead && (
        <LeadFollowUpModal
          open={Boolean(selectedLead)}
          onClose={() => setSelectedLead(null)}
          lead={selectedLead}
          groupId={group.id}
        />
      )}
    </>
  );
};

export default LeadListModal;
